/**
 * Logique métier de calcul des statistiques PDF.
 */
import { CustomRiskAxis, CustomRiskLevel } from "src/core/constants/risk";
import { RiskItem } from "src/core/gql/graphql";
import type {
  DashboardCounts,
  DossierComplet,
  FolderResult,
  RiskDistribution,
  ValidatedFoldersStatistics,
} from "src/core/hooks/folders-list/folder-risk-statistics/types";
import {
  computeCustomerDisplayName,
  computeExtractionDate,
  computeOwnerDisplayName,
  safePercent2Decimals,
} from "src/core/hooks/folders-list/folder-risk-statistics/utils";
import { mapRiskLevelToCustomRiskLevel } from "src/core/mappers/risk.mapper";
import { formatIsoToDate } from "src/core/utils/date";

/**
 * Calcul la répartition des risques entre les différents niveaux (LOW, MEDIUM, HIGH).
 */
export const calculateRiskDistribution = (riskLevels: CustomRiskLevel[]): RiskDistribution => {
  const total = riskLevels.length;

  const counts = riskLevels.reduce(
    (acc, level) => {
      if (level === CustomRiskLevel.LOW) acc.low += 1;
      else if (level === CustomRiskLevel.MEDIUM || level === CustomRiskLevel.NORMAL)
        acc.medium += 1;
      else if (level === CustomRiskLevel.HIGH) acc.high += 1;
      return acc;
    },
    { low: 0, medium: 0, high: 0 }
  );

  return {
    low: { count: counts.low, percentage: safePercent2Decimals(counts.low, total) },
    medium: { count: counts.medium, percentage: safePercent2Decimals(counts.medium, total) },
    high: { count: counts.high, percentage: safePercent2Decimals(counts.high, total) },
  };
};

/**
 * Règle métier : un dossier est “sensible” si :
 * - axe Client contient un risque PEP ou Sanction
 * - OU pays d’enregistrement en risque élevé
 * - OU au moins un stakeholder a un pays à risque élevé
 */
export const isDossierSensible = (
  result: FolderResult,
  highRiskCountryCodes: Set<string>
): boolean => {
  const axesStatus = result.lastModifiedFolderFile?.riskStatus?.riskDetail?.axesStatus;

  // 1) Axe Client => PEP / Sanction
  const clientAxis = axesStatus?.find((axis) => axis.axisName === CustomRiskAxis.CLIENT);
  const hasPepOrSanction =
    clientAxis?.risks?.some(
      (risk) => risk.item === RiskItem.Pep || risk.item === RiskItem.Sanction
    ) ?? false;

  if (hasPepOrSanction) return true;

  // 2) Pays d’enregistrement à risque élevé
  const registrationCountryCode = result.currentCustomer?.registrationCountryCode?.stringValue;
  if (registrationCountryCode && highRiskCountryCodes.has(registrationCountryCode)) return true;

  // 3) Stakeholders => pays à risque élevé
  const stakeholders = result.lastModifiedFolderFile?.fileCustomerFolder?.stakeholders ?? [];
  const hasStakeholderWithHighRiskCountry = stakeholders.some((stakeholder) => {
    const code = stakeholder?.residenceCountryCode?.stringValue;
    return Boolean(code && highRiskCountryCodes.has(code));
  });

  return hasStakeholderWithHighRiskCountry;
};

/**
 * Récupère les niveaux de risque pour un axe donné.
 */
const getRiskLevelsByAxis = (
  results: FolderResult[],
  axisName: CustomRiskAxis
): CustomRiskLevel[] =>
  results.map((result) => {
    const axesStatus = result.lastModifiedFolderFile?.riskStatus?.riskDetail?.axesStatus;
    const axis = axesStatus?.find((axe) => axe.axisName === axisName);
    return mapRiskLevelToCustomRiskLevel(axis?.riskLevel);
  });

/**
 * Calcul les statistiques du PDF.
 */
export const calculateStatistics = (params: {
  results: FolderResult[] | undefined;
  highRiskCountryCodes: Set<string>;
  dashboardCounts: DashboardCounts;
}): ValidatedFoldersStatistics | null => {
  const { results, highRiskCountryCodes, dashboardCounts } = params;

  if (!results || results.length === 0) return null;

  const totalDossiers = results.length;
  const dateExtraction = computeExtractionDate();

  // --- Risques (global + axes)
  const globalRiskLevels = results.map((result) =>
    mapRiskLevelToCustomRiskLevel(result.lastModifiedFolderFile?.riskStatus?.globalRiskLevel)
  );

  const repartitionRisques = {
    global: calculateRiskDistribution(globalRiskLevels),
    activite: calculateRiskDistribution(getRiskLevelsByAxis(results, CustomRiskAxis.ACTIVITE)),
    localisation: calculateRiskDistribution(
      getRiskLevelsByAxis(results, CustomRiskAxis.LOCALISATION)
    ),
    client: calculateRiskDistribution(getRiskLevelsByAxis(results, CustomRiskAxis.CLIENT)),
    mission: calculateRiskDistribution(getRiskLevelsByAxis(results, CustomRiskAxis.MISSIONS)),
  };

  // --- Modulation
  let modulesCount = 0;

  results.forEach((result) => {
    const forcedRisk = result.lastModifiedFolderFile?.riskStatus?.forcedRisk;
    const isModule = forcedRisk !== null && forcedRisk !== undefined;

    if (!isModule) return;

    modulesCount += 1;
  });

  const nonModulesCount = totalDossiers - modulesCount;

  const repartitionModulation = {
    modules: {
      count: modulesCount,
      percentage: safePercent2Decimals(modulesCount, totalDossiers),
    },
    nonModules: {
      count: nonModulesCount,
      percentage: safePercent2Decimals(nonModulesCount, totalDossiers),
    },
  };

  // --- High risk : sensibles vs non sensibles
  let highRiskTotal = 0;
  let highRiskSensitive = 0;

  results.forEach((result) => {
    const globalRisk = mapRiskLevelToCustomRiskLevel(
      result.lastModifiedFolderFile?.riskStatus?.globalRiskLevel
    );

    if (globalRisk !== CustomRiskLevel.HIGH) return;

    highRiskTotal += 1;

    const sensible = isDossierSensible(result, highRiskCountryCodes);
    if (!sensible) return;

    highRiskSensitive += 1;
  });

  const highRiskNonSensitive = highRiskTotal - highRiskSensitive;

  const repartitionRisquesEleves = {
    sensibles: {
      count: highRiskSensitive,
      percentage: safePercent2Decimals(highRiskSensitive, highRiskTotal),
    },
    nonSensibles: {
      count: highRiskNonSensitive,
      percentage: safePercent2Decimals(highRiskNonSensitive, highRiskTotal),
    },
  };

  // --- Liste complète des dossiers
  const dossiersComplets: DossierComplet[] = results.map((result) => {
    const customer = result.currentCustomer;
    const { isPerson, name } = customer
      ? computeCustomerDisplayName(customer)
      : { isPerson: false, name: null };

    const owner = result.lastModifiedFolderFile?.currentCustomerFolder?.owner;
    const inChargeOf = computeOwnerDisplayName(owner);

    const globalRisk = mapRiskLevelToCustomRiskLevel(
      result.lastModifiedFolderFile?.riskStatus?.globalRiskLevel
    );

    const lastModification = formatIsoToDate(
      result.lastModifiedFolderFile?.adminStatus?.lastModification
    );

    return {
      id: result.lastModifiedFolderFile?.id ?? "",
      name,
      isPerson,
      inChargeOf,
      globalRisk,
      lastModification,
    };
  });

  return {
    nombreTotalDossiers: totalDossiers,
    dateExtraction,
    dashboardCounts,
    repartitionRisques,
    repartitionModulation,
    repartitionRisquesEleves,
    dossiersComplets,
  };
};
